import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { QrCode, Printer, Sparkles } from "lucide-react";
import { printQRLabels } from "@/lib/printHelper";

export default function QRGenerator() {
  const [count, setCount] = useState(5);
  const [productId, setProductId] = useState("none");
  const [products, setProducts] = useState([]);
  const [generated, setGenerated] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/store/products").then(r => setProducts(r.data)).catch(() => {});
  }, []);

  const onGenerate = async (e) => {
    e.preventDefault();
    const n = Number(count);
    if (!Number.isFinite(n) || n < 1 || n > 100) {
      toast.error("Enter a number between 1 and 100.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/store/qr/generate", {
        count: n,
        product_id: productId === "none" ? null : productId,
      });
      setGenerated(data.qr_codes || []);
      toast.success(`Generated ${data.qr_codes.length} QR code${data.qr_codes.length === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const printAll = () => printQRLabels(generated);

  return (
    <div data-testid="qr-generator-page" className="space-y-6 max-w-5xl">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">/ QR Code Generator</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Generate QR Codes</h1>
        <p className="text-slate-600 dark:text-slate-400 mt-1 text-sm">
          Each QR code carries your store name, a unique ID, and a timestamp — automatically.
        </p>
      </div>

      <form onSubmit={onGenerate} className="border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-100/40 dark:bg-slate-900/40 p-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <div>
          <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">Number of QR Codes</Label>
          <Input
            data-testid="qr-count-input"
            type="number"
            min={1}
            max={100}
            required
            value={count}
            onChange={(e) => setCount(e.target.value)}
            className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 mt-1.5"
          />
        </div>
        <div>
          <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">Assign Product (optional)</Label>
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger data-testid="qr-product-select" className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white mt-1.5">
              <SelectValue placeholder="None — generate blank QR codes" />
            </SelectTrigger>
            <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
              <SelectItem value="none" data-testid="qr-product-none">None — blank QR codes</SelectItem>
              {products.map(p => (
                <SelectItem key={p.id} value={p.id} data-testid={`qr-product-opt-${p.id}`}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="submit"
          data-testid="generate-qr-btn"
          disabled={submitting}
          className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium h-10"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {submitting ? "Generating…" : "Generate"}
        </Button>
      </form>

      <div className="border border-slate-200 dark:border-slate-800 rounded-lg bg-slate-100/40 dark:bg-slate-900/40 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-slate-500">/ Last Batch</div>
            <div className="font-display text-lg font-semibold">
              {generated.length === 0 ? "No QR codes generated yet" : `${generated.length} QR code${generated.length === 1 ? "" : "s"}`}
            </div>
          </div>
          {generated.length > 0 && (
            <Button
              data-testid="print-batch-btn"
              onClick={printAll}
              variant="outline"
              className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
            >
              <Printer className="w-4 h-4 mr-2" /> Print Batch
            </Button>
          )}
        </div>

        {generated.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <QrCode className="w-10 h-10 mx-auto mb-3 text-slate-700" />
            <div className="text-sm">Your generated QR codes will appear here.</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {generated.map((q) => (
              <div
                key={q.qr_code_id}
                data-testid={`generated-qr-${q.qr_code_id}`}
                className="bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md p-3 text-center"
              >
                <div className="bg-white p-2 rounded">
                  <img src={q.qr_image} alt={q.qr_code_id} className="w-full aspect-square" />
                </div>
                <div className="mt-2 font-mono-tech text-[11px] text-cyan-300">{q.qr_code_id}</div>
                <div className="text-[10px] text-slate-500 truncate">{q.assigned_product_name || "Blank"}</div>
                <Button
                  size="sm"
                  variant="ghost"
                  data-testid={`print-single-${q.qr_code_id}`}
                  onClick={() => printQRLabels([q])}
                  className="w-full mt-2 h-7 text-xs text-slate-600 dark:text-slate-400 hover:text-slate-900 hover:dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800"
                >
                  <Printer className="w-3 h-3 mr-1" /> Print
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
